import {
  type Chain,
  type DynamicClient,
  type WalletProviderMetadata,
  assertWalletAccountSigningAvailability,
} from '@dynamic-labs-sdk/client';
import {
  DYNAMIC_ICONIC_SPRITE_URL,
  assertDefined,
  createWalletProviderEventEmitter,
  formatWalletProviderGroupKey,
  formatWalletProviderKey,
} from '@dynamic-labs-sdk/client/core';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';
import { Psbt } from 'bitcoinjs-lib';

import type { BitcoinWalletProvider } from '../../../../BitcoinWalletProvider.types';
import { bitcoinExecuteCheckoutTransaction } from '../../../../utils/bitcoinExecuteCheckoutTransaction';
import { bitcoinTransferAmount } from '../../../../utils/bitcoinTransferAmount';
import { createPsbtOptions } from '../../../../utils/createPsbtOptions';
import { getNetworkIdFromNetworkName } from '../../../../utils/getNetworkIdFromNetworkName';
import type { BinanceBitcoinInjectedProvider } from '../BinanceBitcoinInjectedProvider.types';

export const BINANCE_METADATA: WalletProviderMetadata = {
  displayName: 'Binance Wallet',
  icon: `${DYNAMIC_ICONIC_SPRITE_URL}#onekey`,
};

type CreateBinanceBitcoinWalletProviderParams = {
  dynamicClient: DynamicClient;
  injectedProvider: BinanceBitcoinInjectedProvider;
};

export const createBinanceBitcoinWalletProvider = ({
  dynamicClient,
  injectedProvider,
}: CreateBinanceBitcoinWalletProviderParams): BitcoinWalletProvider => {
  const chain: Chain = 'BTC';

  const walletProviderType = WalletProviderEnum.BrowserExtension;

  const key = formatWalletProviderKey({
    chain,
    displayName: BINANCE_METADATA.displayName,
    walletProviderType,
  });

  const connect: BitcoinWalletProvider['connect'] = async () => {
    const accounts = await injectedProvider.requestAccounts();

    const address = accounts[0];
    const publicKey = await injectedProvider.getPublicKey();

    return {
      addresses: [{ address, publicKey }],
    };
  };

  const getActiveNetworkId: BitcoinWalletProvider['getActiveNetworkId'] =
    async () => {
      const network = await injectedProvider.getNetwork();

      return getNetworkIdFromNetworkName(
        { networkName: network },
        dynamicClient
      );
    };

  const getConnectedAddresses: BitcoinWalletProvider['getConnectedAddresses'] =
    async () => {
      const accounts = await injectedProvider.getAccounts();

      return {
        addresses: accounts,
      };
    };

  const signMessage: BitcoinWalletProvider['signMessage'] = async ({
    message,
    walletAccount,
    protocol,
  }) => {
    assertDefined(walletAccount, 'Wallet account not found');

    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const signedMessage = await injectedProvider.signMessage(message, protocol);

    return {
      signature: signedMessage,
    };
  };

  const signPsbt: BitcoinWalletProvider['signPsbt'] = async ({
    request,
    walletAccount,
  }) => {
    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const psbtFromBase64 = Psbt.fromBase64(request.unsignedPsbtBase64);
    const psbtOptions = createPsbtOptions({ psbt: psbtFromBase64, request });

    const signedPsbtHex = await injectedProvider.signPsbt(
      psbtFromBase64.toHex(),
      {
        autoFinalized: false,
        toSignInputs: psbtOptions.toSignInputs || [],
      }
    );

    return { signedPsbt: Psbt.fromHex(signedPsbtHex).toBase64() };
  };

  const signPsbts: BitcoinWalletProvider['signPsbts'] = async ({
    requests,
    walletAccount,
  }) => {
    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const signedPsbts: string[] = [];

    for (const request of requests) {
      const signedPsbtResponse = await signPsbt({ request, walletAccount });

      if (signedPsbtResponse) {
        signedPsbts.push(signedPsbtResponse.signedPsbt);
      }
    }

    return {
      signedPsbts,
    };
  };

  const { getEventEmitter, cleanupEventEmitter } =
    createWalletProviderEventEmitter({
      removeEventListeners: () => {
        injectedProvider?.removeAllListeners();
      },

      setupEventListeners: ({
        handleAccountsChanged,
        handleNetworkChanged,
      }) => {
        injectedProvider?.on('accountsChanged', (addresses) => {
          handleAccountsChanged({ addresses });
        });

        injectedProvider?.on('networkChanged', (network) => {
          handleNetworkChanged({ networkId: network });
        });
      },

      supportedEvents: ['accountsChanged', 'networkChanged'],
    });

  const terminate: BitcoinWalletProvider['terminate'] = async () => {
    cleanupEventEmitter();
  };

  return {
    chain,
    connect,
    get events() {
      return getEventEmitter();
    },
    executeCheckoutTransaction: (args) =>
      bitcoinExecuteCheckoutTransaction(args, dynamicClient),
    getActiveNetworkId,
    getConnectedAddresses,
    groupKey: formatWalletProviderGroupKey(BINANCE_METADATA.displayName),
    key,
    metadata: BINANCE_METADATA,
    signMessage,
    signPsbt,
    signPsbts,
    terminate,
    transferAmount: (args) => bitcoinTransferAmount(args, dynamicClient),
    walletProviderType,
  };
};
